import {
    AlignmentType,
    BorderStyle,
    convertInchesToTwip,
    Document,
    ExternalHyperlink,
    ImageRun,
    LineRuleType,
    Packer,
    Paragraph,
    ShadingType,
    Table,
    TableCell,
    TableRow,
    TableLayoutType,
    TextRun,
    UnderlineType,
    VerticalAlign as DocxVerticalAlign,
    WidthType,
} from 'docx';
import {defaultMarkdownParser} from 'prosemirror-markdown';
import {
    parseAgenticSectionResponseData,
    stripAgenticSectionCitationMarkers,
} from '../components/prosemirror-editor/core/nodeViews/agenticSection/agenticSectionState';
import {
    getDocumentHeaderExportSections,
    resolveHeaderDataFromExportMeta,
} from './documentHeaderExport';
import {formatBlockClassificationPrefix} from './documentHeaderMeta';
import {
    PREVIEW_COLORS,
    PREVIEW_FONT,
    PREVIEW_FONT_SIZE,
    PREVIEW_PAGE,
    PREVIEW_SPACING,
} from './previewExportStyles';

/**
 * Cross-platform DOCX settings (Word, macOS Pages, Google Docs).
 * Visual tokens mirror previewTheme.js + NodeRenderer (see previewExportStyles.js).
 */
const EXPORT_FONT = PREVIEW_FONT;
const EXPORT_COLORS = PREVIEW_COLORS;
const BODY_SIZE = PREVIEW_FONT_SIZE.bodyHalfPoints;
const BODY_LINE_SPACING = PREVIEW_SPACING.paragraphLine;

const PAGE_WIDTH_DXA = PREVIEW_PAGE.width;
const PAGE_HEIGHT_DXA = PREVIEW_PAGE.height;
const PAGE_MARGIN_DXA = PREVIEW_PAGE.margin;
const CONTENT_WIDTH_DXA = PREVIEW_PAGE.contentWidth;

const LIST_INDENT_HANGING = PREVIEW_SPACING.listHanging;
const NESTED_LIST_INDENT_STEP = PREVIEW_SPACING.listIndent;

const META_CELL_MARGINS = {
    top: PREVIEW_SPACING.metaPaddingTop,
    bottom: PREVIEW_SPACING.metaPaddingBottom,
    left: PREVIEW_SPACING.metaPaddingSide,
    right: PREVIEW_SPACING.metaPaddingSide,
};

/** Preview ReportMetadata uses bgcolor #f9f9f9 — applied per-paragraph for Word/Pages/Docs. */
function metaPanelShading() {
    return {
        fill: EXPORT_COLORS.metaBackground,
        color: EXPORT_COLORS.metaBackground,
        type: ShadingType.CLEAR,
    };
}

const META_LABEL_COLOR = '666666';
const META_SECTION_HEADING_SIZE = 20;
/** PreviewDocumentHeader outer Stack uses spacing={2} → 16px between section blocks. */
const META_SECTION_GAP = PREVIEW_SPACING.paragraphAfter;
/** MUI Divider default on light grey panels (~rgba(0,0,0,0.12)). */
const META_DIVIDER_COLOR = 'E0E0E0';

const META_DIVIDER_BORDER = {
    color: META_DIVIDER_COLOR,
    space: 1,
    style: BorderStyle.SINGLE,
    size: 4,
};

const META_DIVIDER_NO_BORDER = {
    style: BorderStyle.NONE,
    size: 0,
    color: META_DIVIDER_COLOR,
};

const META_CELL_BORDERS = {
    top: {style: BorderStyle.NONE, size: 0, color: EXPORT_COLORS.metaBackground},
    bottom: {style: BorderStyle.NONE, size: 0, color: EXPORT_COLORS.metaBackground},
    left: {style: BorderStyle.NONE, size: 0, color: EXPORT_COLORS.metaBackground},
    right: {style: BorderStyle.NONE, size: 0, color: EXPORT_COLORS.metaBackground},
};

function bodyTextRun(options = {}) {
    return new TextRun({font: EXPORT_FONT, size: BODY_SIZE, ...options});
}

function getHeadingSize(level) {
    if (level === 1) return PREVIEW_FONT_SIZE.headingLevel1HalfPoints;
    if (level === 2) return PREVIEW_FONT_SIZE.headingLevel2HalfPoints;
    return PREVIEW_FONT_SIZE.headingLevel3HalfPoints;
}

function getHeadingColor(level) {
    return level === 1 ? EXPORT_COLORS.headingPrimary : EXPORT_COLORS.headingSecondary;
}

function getHeadingBlockSpacing(level, {inListItem = false, prevType = null, nextType = null} = {}) {
    if (inListItem) {
        return {before: 0, after: PREVIEW_SPACING.paragraphAfter};
    }

    const gutter = PREVIEW_SPACING.headingGutterAfter[level]
        || PREVIEW_SPACING.headingGutterAfter[3];

    let before;
    let after;
    if (level === 1) {
        before = PREVIEW_SPACING.heading1Before;
        after = PREVIEW_SPACING.heading1After;
    } else if (level === 2) {
        before = PREVIEW_SPACING.heading2Before;
        after = PREVIEW_SPACING.heading2After;
    } else {
        before = PREVIEW_SPACING.heading3Before;
        after = PREVIEW_SPACING.heading3After;
    }

    after += gutter;

    if (nextType === 'bullet_list' || nextType === 'ordered_list') {
        after += PREVIEW_SPACING.listBlockMargin;
    }

    if (prevType === 'bullet_list' || prevType === 'ordered_list') {
        before += PREVIEW_SPACING.listBlockMargin;
    }

    return {before, after};
}

function getListIndent(listDepth) {
    return PREVIEW_SPACING.listIndent + (listDepth * NESTED_LIST_INDENT_STEP);
}

function getBulletMarker(listDepth) {
    return listDepth === 0 ? '\u2022\t' : '\u25E6\t';
}

/** Word uses max(before, after) between adjacent paragraphs — emit explicit gap blocks. */
function createBlockGap(afterTwips) {
    if (!afterTwips || afterTwips <= 0) return null;
    return new Paragraph({
        spacing: {
            before: 0,
            after: afterTwips,
            line: 240,
            lineRule: LineRuleType.AUTO,
        },
    });
}

function normalizeBlockType(node) {
    if (!node?.type) return null;
    return normalizeNodeType(node.type);
}

function applyManualListFormatting(paragraphOptions, children, listStyle, orderNumber, applyListMarker, inListItem, listDepth = 0) {
    const listIndent = getListIndent(listDepth);

    if (applyListMarker && listStyle === 'bullet') {
        paragraphOptions.indent = {left: listIndent, hanging: LIST_INDENT_HANGING};
        children.unshift(bodyTextRun({text: getBulletMarker(listDepth)}));
        return;
    }

    if (applyListMarker && listStyle === 'ordered' && orderNumber != null) {
        paragraphOptions.indent = {left: listIndent, hanging: LIST_INDENT_HANGING};
        children.unshift(bodyTextRun({text: `${orderNumber}.\t`}));
        return;
    }

    if (listStyle && inListItem) {
        paragraphOptions.indent = {left: listIndent};
    }
}

const META_DIVIDER_LINE = Object.freeze({type: 'metaDividerLine'});

function isMetaDividerLineBlock(block) {
    return block === META_DIVIDER_LINE;
}

/** Sentinel between header sections — not a nested w:tbl (MINSKY-17279). */
function metaDividerLine() {
    return META_DIVIDER_LINE;
}

function splitMetadataPanelRows(blocks) {
    const rows = [];
    let current = [];
    for (const block of blocks || []) {
        if (isMetaDividerLineBlock(block)) {
            rows.push({paragraphs: current, showBottomDivider: true});
            current = [];
            continue;
        }
        current.push(block);
    }
    if (current.length > 0) {
        rows.push({paragraphs: current, showBottomDivider: false});
    }
    return rows;
}

function createMetaPanelCell(paragraphs, {showBottomDivider, isFirstRow, isLastRow}) {
    return new TableCell({
        width: {size: CONTENT_WIDTH_DXA, type: WidthType.DXA},
        shading: metaPanelShading(),
        margins: {
            top: isFirstRow ? META_CELL_MARGINS.top : 0,
            bottom: isLastRow ? META_CELL_MARGINS.bottom : 0,
            left: META_CELL_MARGINS.left,
            right: META_CELL_MARGINS.right,
        },
        borders: {
            top: META_CELL_BORDERS.top,
            left: META_CELL_BORDERS.left,
            right: META_CELL_BORDERS.right,
            bottom: showBottomDivider ? META_DIVIDER_BORDER : META_CELL_BORDERS.bottom,
        },
        children: paragraphs.length > 0 ? paragraphs : [new Paragraph({})],
    });
}

function createCompatibleMetadataTable(blocks) {
    const rowSpecs = splitMetadataPanelRows(blocks);
    const rows = rowSpecs.length > 0
        ? rowSpecs
        : [{paragraphs: [new Paragraph({})], showBottomDivider: false}];

    return new Table({
        width: {size: CONTENT_WIDTH_DXA, type: WidthType.DXA},
        columnWidths: [CONTENT_WIDTH_DXA],
        layout: TableLayoutType.FIXED,
        borders: {
            top: META_DIVIDER_NO_BORDER,
            bottom: META_DIVIDER_NO_BORDER,
            left: META_DIVIDER_NO_BORDER,
            right: META_DIVIDER_NO_BORDER,
            insideHorizontal: META_DIVIDER_NO_BORDER,
            insideVertical: META_DIVIDER_NO_BORDER,
        },
        rows: rows.map((spec, index) => new TableRow({
            children: [
                createMetaPanelCell(spec.paragraphs, {
                    showBottomDivider: spec.showBottomDivider,
                    isFirstRow: index === 0,
                    isLastRow: index === rows.length - 1,
                }),
            ],
        })),
    });
}

/**
 * Main export function to generate a DOCX file from TipTap JSON content
 * @param {Object} content - TipTap JSON document content
 * @param {Object} meta - Report metadata (title, authors, classification, etc.)
 * @param {Array} references - Array of reference objects
 * @param {String} title - Report title
 * @returns {Promise<Blob>} - DOCX file as a Blob
 */
export async function exportReportToDocx(content, meta, references, title) {
    try {
        const referenceMap = await buildReferenceMap(content, references);

        const children = [];

        children.push(...createTitleSection(meta, title));

        const documentNodes = getDocumentContentNodes(content);
        if (documentNodes.length > 0) {
            const figureCounter = {count: 0};
            const tableCounter = {count: 0};

            for (let i = 0; i < documentNodes.length; i++) {
                const blockContext = {
                    prevType: normalizeBlockType(documentNodes[i - 1]),
                    nextType: normalizeBlockType(documentNodes[i + 1]),
                };
                const elements = await processNode(
                    documentNodes[i],
                    referenceMap,
                    figureCounter,
                    tableCounter,
                    false,
                    0,
                    blockContext,
                );
                children.push(...elements);
            }
        }

        if (referenceMap.orderedReferences.length > 0) {
            children.push(...createReferencesSection(referenceMap.orderedReferences, meta?.classification || {}));
        }

        const firstAuthor = (meta?.authors || [])[0];
        const creatorName = firstAuthor?.displayName || firstAuthor?.name || 'Report System';

        const doc = new Document({
            creator: creatorName,
            title: title || meta?.reportTitle || 'Report',
            styles: {
                default: {
                    document: {
                        run: {font: EXPORT_FONT, size: BODY_SIZE},
                        paragraph: {
                            spacing: {
                                after: PREVIEW_SPACING.paragraphAfter,
                                line: BODY_LINE_SPACING,
                                lineRule: LineRuleType.AUTO,
                            },
                        },
                    },
                },
            },
            sections: [
                {
                    properties: {
                        page: {
                            size: {
                                width: PAGE_WIDTH_DXA,
                                height: PAGE_HEIGHT_DXA,
                            },
                            margin: {
                                top: PAGE_MARGIN_DXA,
                                right: PAGE_MARGIN_DXA,
                                bottom: PAGE_MARGIN_DXA,
                                left: PAGE_MARGIN_DXA,
                            },
                        },
                    },
                    children: children,
                },
            ],
        });

        const blob = await Packer.toBlob(doc);
        return blob;
    } catch (error) {
        console.error('Error generating DOCX:', error);
        throw error;
    }
}

/**
 * Build a map of references with sequential numbering
 */
async function buildReferenceMap(content, references) {
    const markOrder = [];
    const referencesByMarkId = new Map();

    function collectReferences(node) {
        if (!node) return;

        if (node.type === 'text' && node.marks) {
            for (const mark of node.marks) {
                if ((mark.type === 'reference' || mark.type === 'reportFragment') && mark.attrs?.uid) {
                    markOrder.push(mark.attrs.uid);
                }
            }
        }

        if (node.content && Array.isArray(node.content)) {
            for (const child of node.content) {
                collectReferences(child);
            }
        }
    }

    if (content && content.content) {
        for (const node of content.content) {
            collectReferences(node);
        }
    }

    if (references && references.length > 0) {
        references.forEach((ref) => {
            const markId = ref.referenceMarkId;
            if (!markId) return;
            if (!referencesByMarkId.has(markId)) {
                referencesByMarkId.set(markId, []);
            }
            referencesByMarkId.get(markId).push(ref);
        });
    }

    const numbersByLocalId = new Map();
    const seenLocalIds = new Set();
    const orderedReferences = [];
    let nextNumber = 1;

    markOrder.forEach((markId) => {
        const refs = referencesByMarkId.get(markId);
        if (!refs) return;

        refs.forEach((ref) => {
            const localId = ref.localId;
            if (!localId) return;

            if (!numbersByLocalId.has(localId)) {
                numbersByLocalId.set(localId, nextNumber);
                nextNumber++;
            }

            if (!seenLocalIds.has(localId)) {
                seenLocalIds.add(localId);
                orderedReferences.push({...ref, refNumber: numbersByLocalId.get(localId)});
            }
        });
    });

    const markIdToNumbers = new Map();
    markOrder.forEach((markId) => {
        const refs = referencesByMarkId.get(markId);
        if (!refs) return;

        const numbers = refs
            .map(ref => numbersByLocalId.get(ref.localId))
            .filter(num => num !== null && num !== undefined);

        if (numbers.length > 0 && !markIdToNumbers.has(markId)) {
            markIdToNumbers.set(markId, numbers);
        }
    });

    return {orderedReferences, markIdToNumbers};
}

function metaFieldParagraph(label, value, {boldValue = false, sectionEnd = false, icrTitle = false} = {}) {
    const children = icrTitle
        ? [bodyTextRun({text: `${label}: ${value}`, bold: true})]
        : [
            bodyTextRun({text: `${label}: `, bold: true, color: META_LABEL_COLOR}),
            bodyTextRun({text: value, bold: boldValue}),
        ];

    return new Paragraph({
        children,
        shading: metaPanelShading(),
        spacing: {
            after: sectionEnd ? META_SECTION_GAP : 80,
            line: 276,
            lineRule: LineRuleType.AUTO,
        },
    });
}

function metaSectionHeadingParagraph(title, {spacingBefore = 0} = {}) {
    return new Paragraph({
        children: [
            bodyTextRun({
                text: title.toUpperCase(),
                bold: true,
                size: META_SECTION_HEADING_SIZE,
                color: META_LABEL_COLOR,
            }),
        ],
        shading: metaPanelShading(),
        spacing: {
            before: spacingBefore,
            after: 80,
            line: 276,
            lineRule: LineRuleType.AUTO,
        },
    });
}

function buildMetadataParagraphs(meta) {
    const headerData = resolveHeaderDataFromExportMeta(meta);
    const sections = getDocumentHeaderExportSections({
        headerData,
        authors: meta?.authors || [],
        icrNumber: meta?.icrNumber,
    });

    if (!sections.length) return [];

    const paragraphs = [];
    let afterSectionEnd = false;
    let afterIcrTitle = false;

    sections.forEach((section) => {
        if (section.type === 'heading') {
            const spacingBefore = afterSectionEnd
                ? META_SECTION_GAP
                : (afterIcrTitle ? META_SECTION_GAP : 0);
            paragraphs.push(metaSectionHeadingParagraph(section.title, {spacingBefore}));
            afterSectionEnd = false;
            afterIcrTitle = false;
            return;
        }
        if (section.type === 'field') {
            paragraphs.push(metaFieldParagraph(section.label, section.value, {
                boldValue: section.boldValue,
                sectionEnd: section.sectionEnd === true,
                icrTitle: section.icrTitle === true,
            }));
            if (section.sectionEnd === true) {
                paragraphs.push(metaDividerLine());
            }
            afterSectionEnd = section.sectionEnd === true;
            afterIcrTitle = section.icrTitle === true;
        }
    });

    return paragraphs;
}

/**
 * Create title and metadata section matching ReportContent + ReportMetadata preview layout.
 */
function createTitleSection(meta, title) {
    const sections = [];
    const titleText = (meta?.reportTitle || meta?.title || title || 'Untitled Report').toUpperCase();

    sections.push(new Paragraph({
        children: [bodyTextRun({
            text: titleText,
            size: PREVIEW_FONT_SIZE.titleHalfPoints,
            bold: true,
            color: EXPORT_COLORS.title,
        })],
        alignment: AlignmentType.LEFT,
        spacing: {after: PREVIEW_SPACING.titleAfter, line: 480, lineRule: LineRuleType.AUTO},
    }));

    const metaParagraphs = buildMetadataParagraphs(meta);
    if (metaParagraphs.length > 0) {
        sections.push(createCompatibleMetadataTable(metaParagraphs));
        sections.push(new Paragraph({text: '', spacing: {after: PREVIEW_SPACING.metaBlockAfter}}));
    }

    return sections;
}

/**
 * Normalize ProseMirror node type strings for consistent switch handling.
 */
function normalizeNodeType(type) {
    if (!type) return type;
    const normalized = String(type).toLowerCase();
    const aliases = {
        bulletlist: 'bullet_list',
        orderedlist: 'ordered_list',
        listitem: 'list_item',
        hardbreak: 'hard_break',
        tableheader: 'table_header',
        horizontalrule: 'horizontal_rule',
        linkembed: 'link_embed',
        codeblock: 'code_block',
    };
    return aliases[normalized] || type;
}

/**
 * Extract top-level block nodes from a PM JSON document payload.
 */
function getDocumentContentNodes(content) {
    if (!content) return [];
    if (Array.isArray(content)) return content;
    if (content.type === 'doc' && Array.isArray(content.content)) return content.content;
    if (Array.isArray(content.content)) return content.content;
    return [];
}

function findFirstPrefixTargetIndex(children = []) {
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        if ((child?.type === 'paragraph' || child?.type === 'heading') && (child?.content?.length > 0)) {
            return i;
        }
    }
    return -1;
}

function collectBlockElements(elements = []) {
    return elements.filter((element) => element instanceof Paragraph || element instanceof Table);
}

async function processTableCellContent(cellNode, referenceMap, figureCounter, tableCounter) {
    const cellChildren = [];

    if (cellNode.content) {
        for (const contentNode of cellNode.content) {
            const blockElements = await processNode(contentNode, referenceMap, figureCounter, tableCounter);
            cellChildren.push(...collectBlockElements(blockElements));
        }
    }

    if (cellChildren.length === 0) {
        cellChildren.push(new Paragraph({text: ''}));
    }

    return cellChildren;
}

function parseAgenticSectionMarkdownNodes(markdown) {
    const trimmed = (markdown || '').trim();
    if (!trimmed) return [];

    try {
        const parsed = defaultMarkdownParser.parse(trimmed);
        const nodes = [];
        parsed.content.forEach((child) => {
            try {
                nodes.push(child.toJSON());
            } catch (_error) {
                const text = child.textContent || '';
                if (text.trim()) {
                    nodes.push({type: 'paragraph', content: [{type: 'text', text}]});
                }
            }
        });
        return nodes;
    } catch (error) {
        console.warn('[docxExport] Failed to parse agentic section markdown, falling back to plain text:', error);
        return trimmed.split(/\n{2,}/).map((paragraph) => ({
            type: 'paragraph',
            content: [{type: 'text', text: paragraph.trim()}],
        })).filter((node) => node.content[0].text);
    }
}

/**
 * Process a TipTap node and convert to DOCX elements
 */
async function processNode(
    node,
    referenceMap,
    figureCounter,
    tableCounter,
    inListItem = false,
    listDepth = 0,
    blockContext = null,
) {
    if (!node || !node.type) return [];

    const nodeType = normalizeNodeType(node.type);
    const elements = [];

    switch (nodeType) {
        case 'title':
            break;

        case 'heading':
            elements.push(...processHeading(node, referenceMap, {inListItem, listDepth, blockContext}));
            break;

        case 'paragraph':
            elements.push(...await processParagraph(node, referenceMap, inListItem, figureCounter, {listDepth, blockContext}));
            break;

        case 'bullet_list':
            elements.push(...await processBulletList(node, referenceMap, figureCounter, tableCounter, listDepth, blockContext));
            break;

        case 'ordered_list':
            elements.push(...await processOrderedList(node, referenceMap, figureCounter, tableCounter, listDepth, blockContext));
            break;

        case 'list_item':
            elements.push(...await processListItem(node, referenceMap, figureCounter, tableCounter, null, null, listDepth));
            break;

        case 'image':
            elements.push(...await processImage(node, figureCounter));
            break;

        case 'table':
            elements.push(...await processTable(node, referenceMap, figureCounter, tableCounter));
            break;

        case 'blockquote':
            elements.push(...await processBlockquote(node, referenceMap, figureCounter, tableCounter));
            break;

        case 'code_block':
            elements.push(...processCodeBlock(node));
            break;

        case 'horizontal_rule':
            elements.push(new Paragraph({
                children: [new TextRun({text: '―'.repeat(40), color: '999999'})],
                alignment: AlignmentType.CENTER,
                spacing: {before: 120, after: 120},
            }));
            break;

        case 'link_embed':
            elements.push(...processLinkEmbed(node));
            break;

        case 'agenticSection':
            elements.push(...await processAgenticSection(node, referenceMap, figureCounter, tableCounter));
            break;

        default:
            if (node.content && Array.isArray(node.content)) {
                for (const child of node.content) {
                    elements.push(...await processNode(
                        child,
                        referenceMap,
                        figureCounter,
                        tableCounter,
                        inListItem,
                        listDepth,
                        blockContext,
                    ));
                }
            }
    }

    return elements;
}

async function processAgenticSection(node, referenceMap, figureCounter, tableCounter) {
    const responseData = parseAgenticSectionResponseData(
        node.attrs?.responseText,
        node.attrs?.responseObject,
    ) || {markdown: '', citations: []};
    const markdown = stripAgenticSectionCitationMarkers(responseData.markdown || '');
    const markdownNodes = parseAgenticSectionMarkdownNodes(markdown);
    const elements = [];

    for (let i = 0; i < markdownNodes.length; i++) {
        const childContext = {
            prevType: normalizeBlockType(markdownNodes[i - 1]),
            nextType: normalizeBlockType(markdownNodes[i + 1]),
        };
        elements.push(...await processNode(
            markdownNodes[i],
            referenceMap,
            figureCounter,
            tableCounter,
            false,
            0,
            childContext,
        ));
    }

    return elements;
}

function processLinkEmbed(node) {
    const attrs = node.attrs || {};
    const url = attrs.url || '';
    const title = attrs.title || attrs.siteName || url;
    if (!url && !title) return [];

    const children = [];
    if (title) {
        children.push(new TextRun({text: title, bold: true, size: 22}));
    }
    if (url) {
        if (children.length > 0) {
            children.push(new TextRun({text: '', break: 1}));
        }
        children.push(new ExternalHyperlink({
            children: [new TextRun({
                text: url,
                size: 20,
                color: EXPORT_COLORS.link,
                underline: {type: UnderlineType.SINGLE},
            })],
            link: url,
        }));
    }
    if (attrs.description) {
        children.push(new TextRun({text: '', break: 1}));
        children.push(new TextRun({text: attrs.description, size: 20, color: '666666'}));
    }

    return [new Paragraph({children, spacing: {before: 120, after: 240}})];
}

function processCodeBlock(node) {
    const text = (node.content || [])
        .map((child) => child.text || '')
        .join('');

    return [
        new Paragraph({
            children: [new TextRun({text, font: 'Courier New', size: 20})],
            shading: {type: ShadingType.SOLID, fill: 'F5F5F5', color: 'auto'},
            spacing: {before: 120, after: 240},
        }),
    ];
}

async function processListItem(
    listItem,
    referenceMap,
    figureCounter,
    tableCounter,
    listStyle = null,
    orderNumber = null,
    listDepth = 0,
    listPosition = {},
) {
    const elements = [];
    const content = listItem.content || [];
    const firstIdx = findFirstPrefixTargetIndex(content);
    const classification = getClassification(listItem);
    const prefixText = content.length > 0 ? `(${classification})` : undefined;
    let listMarkerApplied = false;

    for (let i = 0; i < content.length; i++) {
        const child = content[i];
        const childPrefix = i === firstIdx ? prefixText : undefined;
        const applyListMarker = !listMarkerApplied
            && listStyle
            && (child.type === 'paragraph' || child.type === 'heading');
        const childType = normalizeNodeType(child.type);

        if (child.type === 'paragraph') {
            const paragraphElements = await processParagraph(
                child,
                referenceMap,
                true,
                figureCounter,
                {
                    prefixText: childPrefix,
                    listStyle,
                    orderNumber,
                    applyListMarker,
                    listDepth,
                    spacingBefore: 0,
                    spacingAfter: PREVIEW_SPACING.paragraphAfter
                        + (i === content.length - 1 && !(content[i + 1]?.type === 'bullet_list' || content[i + 1]?.type === 'ordered_list')
                            ? PREVIEW_SPACING.listItemAfter
                            : 0),
                },
            );
            elements.push(...paragraphElements);
        } else if (child.type === 'heading') {
            const headingElements = processHeading(child, referenceMap, {
                inListItem: true,
                prefixText: childPrefix,
                listStyle,
                orderNumber,
                applyListMarker,
                listDepth,
            });
            elements.push(...headingElements);
        } else if (childType === 'bullet_list') {
            const gap = createBlockGap(PREVIEW_SPACING.nestedListGap);
            if (gap) elements.push(gap);
            elements.push(...await processBulletList(child, referenceMap, figureCounter, tableCounter, listDepth + 1));
        } else if (childType === 'ordered_list') {
            const gap = createBlockGap(PREVIEW_SPACING.nestedListGap);
            if (gap) elements.push(gap);
            elements.push(...await processOrderedList(child, referenceMap, figureCounter, tableCounter, listDepth + 1));
        } else {
            elements.push(...await processNode(child, referenceMap, figureCounter, tableCounter, true, listDepth));
        }

        if (applyListMarker) {
            listMarkerApplied = true;
        }
    }

    return elements;
}

/**
 * Get classification prefix string for a node
 */
function getClassification(node) {
    return formatBlockClassificationPrefix(node?.attrs);
}

/**
 * Process heading node
 */
function processHeading(node, referenceMap, options = {}) {
    const {
        inListItem = false,
        prefixText,
        listStyle = null,
        orderNumber = null,
        applyListMarker = false,
        listDepth = 0,
        blockContext = null,
    } = options;
    const level = node.attrs?.level || 1;
    const classification = getClassification(node);
    const headingSize = getHeadingSize(level);
    const headingColor = getHeadingColor(level);

    const children = [];

    if (!inListItem && (node.content?.length > 0)) {
        children.push(bodyTextRun({
            text: `(${classification}) `,
            bold: true,
            size: headingSize,
            color: headingColor,
        }));
    }

    if (prefixText) {
        children.push(bodyTextRun({
            text: `${prefixText} `,
            bold: true,
            size: headingSize,
            color: headingColor,
        }));
    }

    if (node.content) {
        children.push(...processInlineContent(node.content, referenceMap, true, level, headingColor));
    }

    const headingSpacing = getHeadingBlockSpacing(level, {
        inListItem,
        prevType: blockContext?.prevType || null,
        nextType: blockContext?.nextType || null,
    });

    const elements = [];

    if (!inListItem && blockContext?.prevType === 'paragraph') {
        const twoEnterGap = createBlockGap(PREVIEW_SPACING.headingBeforeParagraphGap);
        if (twoEnterGap) elements.push(twoEnterGap);
    }

    const paragraphOptions = {
        children,
        spacing: headingSpacing,
    };

    applyManualListFormatting(
        paragraphOptions,
        children,
        listStyle,
        orderNumber,
        applyListMarker,
        inListItem,
        listDepth,
    );

    elements.push(new Paragraph(paragraphOptions));

    if (!inListItem && blockContext?.nextType === 'paragraph') {
        const afterHeadingGap = createBlockGap(PREVIEW_SPACING.headingAfterParagraph);
        if (afterHeadingGap) elements.push(afterHeadingGap);
    }

    return elements;
}

/**
 * Process paragraph node.
 * Images are inline in the schema — they are lifted out as block DOCX elements.
 */
async function processParagraph(node, referenceMap, inListItem = false, figureCounter = {count: 0}, options = {}) {
    const {
        prefixText,
        listStyle = null,
        orderNumber = null,
        applyListMarker = false,
        listDepth = 0,
        spacingBefore = 0,
        spacingAfter = PREVIEW_SPACING.paragraphAfter,
    } = options;
    const classification = getClassification(node);
    const contentNodes = node.content || [];

    const hasImages = contentNodes.some(c => c.type === 'image');

    if (hasImages) {
        const elements = [];
        let inlineBuffer = [];

        const flushInline = () => {
            if (inlineBuffer.length === 0) return;
            const runs = processInlineContent(inlineBuffer, referenceMap, false);
            if (runs.length > 0) {
                elements.push(new Paragraph({children: runs, spacing: {before: 0, after: 120}}));
            }
            inlineBuffer = [];
        };

        for (const child of contentNodes) {
            if (child.type === 'image') {
                flushInline();
                elements.push(...await processImage(child, figureCounter));
            } else {
                inlineBuffer.push(child);
            }
        }
        flushInline();

        return elements;
    }

    const children = [];

    if (prefixText) {
        children.push(bodyTextRun({text: `${prefixText} `}));
    } else if (!inListItem && contentNodes.length > 0) {
        const hasText = contentNodes.some(c => c.type === 'text' && c.text && c.text.trim());
        if (hasText) {
            children.push(bodyTextRun({text: `(${classification}) `}));
        }
    }

    if (contentNodes.length > 0) {
        children.push(...processInlineContent(contentNodes, referenceMap, false));
    }

    if (children.length === 0) {
        return [new Paragraph({text: ''})];
    }

    const paragraphOptions = {
        children,
        spacing: {
            before: spacingBefore,
            after: spacingAfter,
            line: BODY_LINE_SPACING,
            lineRule: LineRuleType.AUTO,
        },
    };

    applyManualListFormatting(
        paragraphOptions,
        children,
        listStyle,
        orderNumber,
        applyListMarker,
        inListItem,
        listDepth,
    );

    return [new Paragraph(paragraphOptions)];
}

/**
 * Process inline content (text with marks)
 */
function processInlineContent(content, referenceMap, isBold = false, headingLevel = 0, headingColor = null) {
    const runs = [];
    const runSize = headingLevel === 1
        ? PREVIEW_FONT_SIZE.headingLevel1HalfPoints
        : headingLevel === 2
            ? PREVIEW_FONT_SIZE.headingLevel2HalfPoints
            : headingLevel >= 3
                ? PREVIEW_FONT_SIZE.headingLevel3HalfPoints
                : BODY_SIZE;

    for (const node of content) {
        if (node.type === 'text') {
            const text = node.text || '';
            const marks = node.marks || [];

            const hasBold = marks.some(m => m.type === 'bold' || m.type === 'strong') || isBold;
            const hasItalic = marks.some(m => m.type === 'em' || m.type === 'italic');
            const hasCode = marks.some(m => m.type === 'code');
            const linkMark = marks.find(m => m.type === 'link');
            const userMentionMark = marks.find(m => m.type === 'userMention');
            const hashtagMark = marks.find(m => m.type === 'hashtag');
            const wikidataEntityMark = marks.find(m => m.type === 'wikidataEntity');

            let runColor = headingColor || undefined;
            let runBackground;
            if (userMentionMark) {
                runColor = '3d3d3d';
                runBackground = 'e7e7e7';
            } else if (hashtagMark) {
                runColor = '9c27b0';
                runBackground = 'f3e5f5';
            } else if (wikidataEntityMark) {
                runColor = EXPORT_COLORS.link;
            }

            const textRun = bodyTextRun({
                text,
                bold: hasBold,
                italics: hasItalic,
                font: hasCode ? 'Courier New' : EXPORT_FONT,
                size: runSize,
                color: runColor,
                shading: runBackground ? {type: ShadingType.SOLID, color: runBackground, fill: runBackground} : undefined,
            });

            if (linkMark && linkMark.attrs?.href) {
                runs.push(new ExternalHyperlink({
                    children: [bodyTextRun({
                        text,
                        bold: hasBold,
                        italics: hasItalic,
                        size: runSize,
                        color: EXPORT_COLORS.link,
                        underline: {type: UnderlineType.SINGLE},
                    })],
                    link: linkMark.attrs.href,
                }));
            } else {
                runs.push(textRun);
            }

            const refMark = marks.find(m => m.type === 'reference' || m.type === 'reportFragment');
            if (refMark && referenceMap?.markIdToNumbers) {
                const refNumbers = referenceMap.markIdToNumbers.get(refMark.attrs?.uid);
                if (refNumbers && refNumbers.length > 0) {
                    runs.push(new TextRun({
                        text: `[${refNumbers.join(',')}]`,
                        superScript: true,
                        size: 18,
                        color: '808080',
                    }));
                }
            }
        } else if (node.type === 'hardBreak' || node.type === 'hard_break') {
            runs.push(new TextRun({text: '', break: 1}));
        } else if (node.type === 'wikidataMention') {
            const label = node.attrs?.label || '';
            const url = node.attrs?.url;
            if (label) {
                const textRun = bodyTextRun({text: label, bold: true, color: EXPORT_COLORS.link});
                runs.push(url
                    ? new ExternalHyperlink({children: [textRun], link: url})
                    : textRun
                );
            }
        }
    }

    return runs;
}

/**
 * Process bullet list
 */
async function processBulletList(node, referenceMap, figureCounter, tableCounter, listDepth = 0, blockContext = null) {
    const elements = [];
    if (!node.content) return elements;

    const listItems = node.content.filter((item) => normalizeNodeType(item.type) === 'list_item');

    for (let i = 0; i < listItems.length; i++) {
        elements.push(...await processListItem(
            listItems[i],
            referenceMap,
            figureCounter,
            tableCounter,
            'bullet',
            null,
            listDepth,
            {isFirstInList: i === 0, isLastInList: i === listItems.length - 1},
        ));

        if (listDepth === 0 && i < listItems.length - 1) {
            const gap = createBlockGap(PREVIEW_SPACING.judgmentBlockGap);
            if (gap) elements.push(gap);
        }
    }

    if (listDepth === 0 && listItems.length > 0 && blockContext?.nextType !== 'heading') {
        const gap = createBlockGap(PREVIEW_SPACING.listBlockMargin);
        if (gap) elements.push(gap);
    }

    return elements;
}

/**
 * Process ordered list
 */
async function processOrderedList(node, referenceMap, figureCounter, tableCounter, listDepth = 0, blockContext = null) {
    const elements = [];
    if (!node.content) return elements;

    const start = (node.attrs && node.attrs.start) || 1;
    let orderNumber = start - 1;
    const listItems = node.content.filter((item) => normalizeNodeType(item.type) === 'list_item');

    for (let i = 0; i < listItems.length; i++) {
        orderNumber += 1;
        elements.push(...await processListItem(
            listItems[i],
            referenceMap,
            figureCounter,
            tableCounter,
            'ordered',
            orderNumber,
            listDepth,
            {isFirstInList: i === 0, isLastInList: i === listItems.length - 1},
        ));

        if (listDepth === 0 && i < listItems.length - 1) {
            const gap = createBlockGap(PREVIEW_SPACING.judgmentBlockGap);
            if (gap) elements.push(gap);
        }
    }

    if (listDepth === 0 && listItems.length > 0 && blockContext?.nextType !== 'heading') {
        const gap = createBlockGap(PREVIEW_SPACING.listBlockMargin);
        if (gap) elements.push(gap);
    }

    return elements;
}

/**
 * Process blockquote
 */
async function processBlockquote(node, referenceMap, figureCounter, tableCounter) {
    const elements = [];
    if (!node.content) return elements;

    for (const child of node.content) {
        if (child.type === 'paragraph') {
            elements.push(new Paragraph({
                children: processInlineContent(child.content || [], referenceMap),
                indent: {left: convertInchesToTwip(0.5)},
                spacing: {before: 0, after: 120},
            }));
        } else {
            elements.push(...await processNode(child, referenceMap, figureCounter, tableCounter));
        }
    }

    return elements;
}

/**
 * Derive a docx-compatible image type string from a MIME type or file path.
 * docx 8.x accepts: 'jpg', 'png', 'gif', 'bmp', 'svg', 'webp'.
 */
function imageTypeFromMime(mimeType, src) {
    const mime = (mimeType || '').toLowerCase();
    if (mime.includes('png')) return 'png';
    if (mime.includes('gif')) return 'gif';
    if (mime.includes('bmp')) return 'bmp';
    if (mime.includes('svg')) return 'svg';
    if (mime.includes('webp')) return 'webp';
    if (mime.includes('jpeg') || mime.includes('jpg')) return 'jpg';

    // Fall back to extension from the URL / data URI
    const ext = (src || '').split('?')[0].split('.').pop().toLowerCase();
    if (['png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return ext;
    if (['jpg', 'jpeg'].includes(ext)) return 'jpg';

    return 'jpg'; // safe default
}

/**
 * Fetch an image from any URL (relative or absolute) and return
 * { buffer: ArrayBuffer, type: string } for use with docx ImageRun.
 */
async function fetchImageData(src) {
    try {
        const response = await fetch(src);
        if (!response.ok) return null;
        const buffer = await response.arrayBuffer();
        const contentType = response.headers.get('content-type') || '';
        return {buffer, type: imageTypeFromMime(contentType, src)};
    } catch (e) {
        console.error('Failed to fetch image:', src, e);
        return null;
    }
}

/**
 * Process image node
 */
async function processImage(node, figureCounter) {
    const elements = [];

    if (!node.attrs || !node.attrs.src) return elements;

    figureCounter.count++;
    const figureNumber = node.attrs.figureNumber || figureCounter.count;
    const caption = node.attrs.caption || '';
    const classification = getClassification(node);

    try {
        const src = node.attrs.src;
        let imageData = null;
        let imageType = 'jpg';

        // Data URI: parse type from the MIME prefix, pass string to docx
        if (src.startsWith('data:image/')) {
            imageData = src;
            const mimeMatch = src.match(/^data:image\/([^;]+);/);
            imageType = mimeMatch ? imageTypeFromMime(`image/${mimeMatch[1]}`, src) : 'jpg';
        } else {
            // Handles absolute URLs (http/https) and relative paths (/api/...).
            const fetched = await fetchImageData(src);
            if (fetched) {
                imageData = fetched.buffer;
                imageType = fetched.type;
            }
        }

        if (imageData) {
            elements.push(new Paragraph({text: ''}));

            elements.push(
                new Paragraph({
                    children: [
                        new ImageRun({
                            data: imageData,
                            type: imageType,
                            transformation: {width: 400, height: 300},
                        }),
                    ],
                    alignment: AlignmentType.CENTER,
                })
            );

            const captionText = caption
                ? `(${classification}) Figure ${figureNumber}. ${caption}`
                : `(${classification}) Figure ${figureNumber}`;

            elements.push(
                new Paragraph({
                    children: [new TextRun({text: captionText, size: 18, color: '808080'})],
                    alignment: AlignmentType.CENTER,
                    spacing: {before: 0, after: 0},
                })
            );

            elements.push(new Paragraph({text: ''}));
        }
    } catch (error) {
        console.error('Error processing image:', error);
    }

    return elements;
}

/**
 * Process table node
 */
async function processTable(node, referenceMap, figureCounter, tableCounter) {
    const elements = [];

    if (!node.content || node.content.length === 0) return elements;

    tableCounter.count++;
    const classification = getClassification(node);
    const caption = node?.attrs?.caption;
    const captionClassification = node?.attrs?.classification_access;

    // Determine column count by scanning all rows (handles colspan merges)
    let numCols = 0;
    for (const rowNode of node.content) {
        if (normalizeNodeType(rowNode.type) === 'table_row' && rowNode.content) {
            let rowCols = 0;
            for (const cellNode of rowNode.content) {
                const cellType = normalizeNodeType(cellNode.type);
                if (cellType === 'table_cell' || cellType === 'table_header') {
                    rowCols += (cellNode.attrs?.colspan || 1);
                }
            }
            if (rowCols > numCols) numCols = rowCols;
        }
    }
    if (numCols < 1) numCols = 1;

    const colWidth = Math.floor(CONTENT_WIDTH_DXA / numCols);
    const columnWidths = Array(numCols).fill(colWidth);

    const rows = [];

    for (const rowNode of node.content) {
        if (normalizeNodeType(rowNode.type) === 'table_row' && rowNode.content) {
            const cells = [];

            for (const cellNode of rowNode.content) {
                const cellType = normalizeNodeType(cellNode.type);
                if (cellType === 'table_cell' || cellType === 'table_header') {
                    const colspan = cellNode.attrs?.colspan || 1;
                    const cellWidth = colWidth * colspan;

                    const cellChildren = await processTableCellContent(
                        cellNode,
                        referenceMap,
                        figureCounter,
                        tableCounter,
                    );

                    const cellOptions = {
                        children: cellChildren,
                        verticalAlign: DocxVerticalAlign.TOP,
                        width: {size: cellWidth, type: WidthType.DXA},
                    };

                    if (colspan > 1) {
                        cellOptions.columnSpan = colspan;
                    }

                    cells.push(new TableCell(cellOptions));
                }
            }

            rows.push(new TableRow({children: cells}));
        }
    }

    if (rows.length > 0) {
        if (caption) {
            const captionPrefix = captionClassification ? `(${captionClassification}) ` : '';
            elements.push(new Paragraph({
                children: [new TextRun({text: `${captionPrefix}${caption}`, italics: true, size: 20})],
                spacing: {before: 120, after: 60},
            }));
        }

        elements.push(
            new Table({
                rows,
                width: {size: CONTENT_WIDTH_DXA, type: WidthType.DXA},
                columnWidths,
                layout: TableLayoutType.FIXED,
                borders: {
                    top: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                    bottom: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                    left: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                    right: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                    insideHorizontal: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                    insideVertical: {style: BorderStyle.SINGLE, size: 4, color: '000000'},
                },
            })
        );

        elements.push(
            new Paragraph({
                children: [new TextRun({
                    text: `(${classification}) Table ${tableCounter.count}`,
                    size: 18,
                    color: '808080',
                })],
                alignment: AlignmentType.CENTER,
                spacing: {before: 0, after: 0},
            })
        );

        elements.push(new Paragraph({text: ''}));
    }

    return elements;
}

/**
 * Create references section
 */
function createReferencesSection(orderedReferences, classification) {
    const sections = [];

    sections.push(new Paragraph({text: ''}));

    const classificationValue = typeof classification === 'object'
        ? (classification?.value || classification?.label || '')
        : (typeof classification === 'string' ? classification : '');

    const headingText = classificationValue ? `(${classificationValue}) References` : 'References';
    sections.push(
        new Paragraph({
            children: [bodyTextRun({
                text: headingText,
                size: 42,
                bold: true,
                color: EXPORT_COLORS.headingSecondary,
            })],
            spacing: getHeadingBlockSpacing(2, {nextType: null}),
        })
    );

    for (const ref of orderedReferences) {
        const children = [];

        children.push(bodyTextRun({text: `[${ref.refNumber}] `, superScript: true, size: 18, color: '808080'}));

        const titleText = ref.docTitle || ref.title || ref.name || ref.label || 'Untitled';

        if (ref.docUrl || ref.url) {
            const url = ref.docUrl || ref.url;
            children.push(
                new ExternalHyperlink({
                    children: [bodyTextRun({
                        text: titleText,
                        color: EXPORT_COLORS.link,
                        underline: {type: UnderlineType.SINGLE},
                    })],
                    link: url,
                })
            );
            children.push(new TextRun({text: '', break: 1}));
            children.push(
                new ExternalHyperlink({
                    children: [new TextRun({
                        text: url,
                        size: 16,
                        color: '808080',
                        underline: {type: UnderlineType.SINGLE},
                    })],
                    link: url,
                })
            );
        } else {
            children.push(new TextRun({text: titleText, size: 20}));
        }

        sections.push(new Paragraph({children, spacing: {after: 120}}));
    }

    return sections;
}

export const docxExportInternals = {
    getDocumentContentNodes,
    normalizeNodeType,
    normalizeBlockType,
    parseAgenticSectionMarkdownNodes,
    createCompatibleMetadataTable,
    buildMetadataParagraphs,
    isMetaDividerLineBlock,
    metaDividerLine,
    getDocumentHeaderExportSections,
    createBlockGap,
    getHeadingBlockSpacing,
    CONTENT_WIDTH_DXA,
    buildReferenceMap,
};
